import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';
import { HooksDisplayGrid } from 'ui-components/components/hooks-display-grid';
import GridLoading from '../../../components/grid-loading';
import ErrorBoundary from '../../../components/error-boundary';
import { QuickSearchContext } from '../quicksearch-context';
import { InventoryContext } from './inventory-context';
import baseColumns from './columns';
import Global from '../../../store/global-selectors';
import { saveUserSettingGridConfig } from '../../../store/user/user.actions';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import EmptyGridPlaceholder from './grid-placeholder';
import {
  ActiveShippingLocationsColumnId,
  AuditColumnId,
  baseColumnsDefaultConfigs,
} from '../../../store/inventory/grid/default-column-configuration';
import { useUserSetting } from '../../../hooks/use-user-setting';
import { useShouldDisplayAutoRoutingControls } from '../../../hooks/auto-routing/use-should-display-auto-routing-controls';
import { useEnableStockAudit } from '../../../feature-flags/hooks/use-enable-stock-audit';
import style from '../../grid-page.module.less';

// Filter placeholders
const statusViewId = 'inventory';
const activeFilterViewId = 'default';

const getColumnId = ({ id }) => id;

const buildColumns = (columnConfig) =>
  columnConfig
    .map((col) => {
      const colToRender = baseColumns[col.mappedId] || baseColumns[col.id];
      if (colToRender) {
        return {
          ...col,
          name: colToRender.name,
          render: colToRender.render,
        };
      }
      return null;
    })
    .filter((col) => !!col);

const InventoryGrid = () => {
  const { isLoading: quickSearchLoading } = useContext(QuickSearchContext);
  const { entitiesLoading, inventoryIds, setColumnSort, setSelectedProductIds, selectedProductIds } = useContext(InventoryContext);

  const [expandedRows, setExpandedRows] = useState([]);
  const inventoryEntities = useSelector((state) =>
    Global.selectEntitiesInventoryByProductIds(state, {
      productIds: inventoryIds,
    }),
  );
  const [gridColumns, setGridColumns] = useState([]);

  const isLoading = entitiesLoading || quickSearchLoading;

  const shouldDisplayAutoRoutingControls = useShouldDisplayAutoRoutingControls();
  const isStockAuditEnabled = useEnableStockAudit();

  const applyColumnConfigurationFilters = useCallback(
    (column) => {
      if (column.id === ActiveShippingLocationsColumnId && shouldDisplayAutoRoutingControls) {
        return true;
      }
      if (column.id === ActiveShippingLocationsColumnId && !shouldDisplayAutoRoutingControls) {
        return false;
      }
      if (column.id === AuditColumnId && !isStockAuditEnabled) {
        return false;
      }

      return true;
    },
    [shouldDisplayAutoRoutingControls, isStockAuditEnabled],
  );

  const userSetColumnConfiguration = useUserSetting(UserSettings.inventoryGridDefaultConfig.settingName);
  const columnConfiguration = useMemo(() => {
    const parsedUserColumnConfiguration = JSON.parse(userSetColumnConfiguration || '[]');
    return parsedUserColumnConfiguration.length
      ? parsedUserColumnConfiguration.map((savedConfig) => {
          const fullConfig = baseColumnsDefaultConfigs.find((c) => c.id === savedConfig.id);
          return { ...fullConfig, ...savedConfig };
        })
      : baseColumnsDefaultConfigs;
  }, [userSetColumnConfiguration]);
  const filteredColumnConfiguration = useMemo(
    () => columnConfiguration.filter(applyColumnConfigurationFilters),
    [columnConfiguration, applyColumnConfigurationFilters],
  );
  useEffect(() => {
    setGridColumns(buildColumns(filteredColumnConfiguration));
  }, [columnConfiguration, filteredColumnConfiguration]);

  const { inventoryWarehouseId } = useParams();

  const gridData = useMemo(() => {
    return (
      inventoryIds?.reduce((acc, id) => {
        const inventoryLocations = inventoryEntities.filter((entity) => entity.productId === id);

        const mappedLocations = inventoryLocations.reduce((innerAcc, il) => {
          if (!inventoryWarehouseId || il.inventoryWarehouseId === inventoryWarehouseId) {
            innerAcc.push({ ...il, id: il.productId });
          }
          return innerAcc;
        }, []);

        if (mappedLocations.length === 1) {
          acc.push(mappedLocations[0]);
        } else if (mappedLocations.length > 0) {
          acc.push(mappedLocations);
        }

        return acc;
      }, []) ?? []
    );
  }, [inventoryEntities, inventoryIds, inventoryWarehouseId]);

  const dispatch = useDispatch();
  const onColumnsChanged = useCallback(
    (columns) => {
      dispatch(saveUserSettingGridConfig('inventory', statusViewId, activeFilterViewId, columns));
      setGridColumns(buildColumns(columns));
    },
    [dispatch],
  );

  const onOrderByChanged = useCallback(
    (columns) => {
      dispatch(saveUserSettingGridConfig('inventory', statusViewId, activeFilterViewId, columns));
      const { id, sorted: sortDirection } = columns.find((col) => col.sorted);
      setColumnSort(id, sortDirection);
      setGridColumns(buildColumns(columns));
    },
    [dispatch, setColumnSort],
  );

  return (
    <div className={style.grid}>
      {isLoading && <GridLoading />}
      {gridData.length === 0 && <EmptyGridPlaceholder />}
      <ErrorBoundary errorMessageId='inventoryGrid'>
        <HooksDisplayGrid
          columns={gridColumns}
          data={gridData}
          expandedRows={expandedRows}
          getColumnId={getColumnId}
          onColumnsChanged={onColumnsChanged}
          onExpandedRowsChanged={setExpandedRows}
          onOrderByChanged={onOrderByChanged}
          onSelectedChanged={setSelectedProductIds}
          selected={selectedProductIds}
        />
      </ErrorBoundary>
    </div>
  );
};

export default InventoryGrid;
